import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Grid,
  OutlinedInput,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { strategiesApi } from '../../api/strategies';
import { botsApi } from '../../api/bots';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../../components/common/EmptyState';

const initialFormData = {
  nombre: '',
  descripcion: '',
  status: 'active',
  tipo: 'recordatorio',
  endpoint: '',
  dias_antes_vencimiento: 0,
  dias_despues_vencimiento: 0,
  hora_envio: '09:00',
  frecuencia: 'daily',
  canal: 'whatsapp',
  id_bot: '',
  plantilla_mensaje: '',
  idioma: 'es',
  filtro_estado_prestamo: 'all',
  dias_minimo_atraso: 0,
  dias_maximo_atraso: 0,
  monto_minimo: 0,
  monto_maximo: 0,
  excluir_pagos_recientes_dias: 0,
  // Configuración de frecuencia por tipo de préstamo
  configuracion_atraso_tiempo:{
    prestamos_diarios_cada_dias: 3, // diarios cada 3 días
    prestamos_semanales_cada_dias: 7, // semanales cada semana (7 días)
    prestamos_quincenales_cada_dias: 15, // quincenales cada 15 días
    prestamos_mensuales_cada_dias: 30, // mensuales cada 30 días
  }
};

const K = {
  blue: '#4185F1',
  border: 'rgba(18, 24, 38, 0.08)',
  surface: '#F8FAFC',
};
const kobraPageSx = { p: 3, width: '100%', maxWidth: { sm: '100%', md: '1700px' }, bgcolor: K.surface };
const kobraPrimaryBtnSx = {
  '&&': {
    borderRadius: 1.5,
    px: 3,
    py: 1.25,
    textTransform: 'none',
    fontWeight: 700,
    bgcolor: K.blue,
    color: '#fff',
    border: 'none',
    backgroundImage: 'none',
    boxShadow: `0 4px 14px ${alpha(K.blue, 0.35)}`,
    '&:hover': {
      bgcolor: '#3574d9',
      border: 'none',
      backgroundImage: 'none',
      boxShadow: `0 6px 18px ${alpha(K.blue, 0.38)}`,
    },
  },
};
const kobraCardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 2,
  border: `1px solid ${K.border}`,
  boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
  transition: 'all .2s ease',
  '&:hover': { boxShadow: '0 6px 18px rgba(18, 24, 38, 0.12)', transform: 'translateY(-2px)' },
};

export const PaymentStrategies = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const queryClient = useQueryClient();
  const { showToast, ToastComponent } = useToast();

  const { data: strategies, isLoading } = useQuery({
    queryKey: ['strategies'],
    queryFn: () => strategiesApi.getStrategies(),
  });

  const { data: bots } = useQuery({
    queryKey: ['bots'],
    queryFn: () => botsApi.getBots(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => strategiesApi.createStrategy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategies'] });
      setDialogOpen(false);
      showToast('Estrategia creada exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al crear estrategia', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => strategiesApi.updateStrategy(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategies'] });
      setDialogOpen(false);
      showToast('Estrategia actualizada exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al actualizar estrategia', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => strategiesApi.deleteStrategy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategies'] });
      showToast('Estrategia eliminada exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al eliminar estrategia', 'error');
    },
  });

  const handleCreate = () => {
    setEditingStrategy(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    setFormData({
      nombre: strategy.nombre || '',
      descripcion: strategy.descripcion || '',
      status: strategy.status || 'active',
      tipo: strategy.tipo || 'recordatorio',
      endpoint: strategy.endpoint || '',
      dias_antes_vencimiento: strategy.dias_antes_vencimiento ?? 0,
      canal: strategy.canal || 'whatsapp',
      id_bot: strategy.id_bot || '',
      plantilla_mensaje: strategy.plantilla_mensaje || '',
      idioma: strategy.idioma || 'es',
      filtro_estado_prestamo: strategy.filtro_estado_prestamo || 'all',
      dias_minimo_atraso: strategy.dias_minimo_atraso ?? 0,
      dias_maximo_atraso: strategy.dias_maximo_atraso ?? 0,
      configuracion_atraso_tiempo:{
        prestamos_diarios_cada_dias: strategy.prestamos_diarios_cada_dias ?? 3,
        prestamos_semanales_cada_dias: strategy.prestamos_semanales_cada_dias ?? 7,
        prestamos_quincenales_cada_dias: strategy.prestamos_quincenales_cada_dias ?? 15,
        prestamos_mensuales_cada_dias: strategy.prestamos_mensuales_cada_dias ?? 30,  
      }
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setEditingStrategy(null);
  };

  const handleSave = () => {
    console.log(formData);
    if (editingStrategy) {
      updateMutation.mutate({ id: editingStrategy.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta estrategia?')) {
      deleteMutation.mutate(id);
    }
  };

  const getTimingInfo = (strategy) => {
    const parts = [];
    if (strategy.dias_antes_vencimiento > 0) {
      parts.push(`${strategy.dias_antes_vencimiento}d antes`);
    }
    if (strategy.hora_envio) {
      parts.push(`a las ${strategy.hora_envio}`);
    }
    return parts.join(', ') || 'Sin configuración';
  };


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', p: 3 }}>
        <Typography color="text.secondary">Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={kobraPageSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(K.blue, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NotificationsIcon sx={{ fontSize: 32, color: K.blue }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
              Estrategias de Pago
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administra y configura tus estrategias de recordatorios de pago
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={kobraPrimaryBtnSx}
        >
          Crear Estrategia
        </Button>
      </Box>
      {!strategies?.length ? (
        <EmptyState
          title="Sin Estrategias"
          description="Crea tu primera estrategia de recordatorio de pago para automatizar las comunicaciones con clientes"
          actionLabel="Crear Estrategia"
          onAction={handleCreate}
          icon={NotificationsIcon}
        />
      ) : (
        <Grid container spacing={3}>
          {strategies.map((strategy) => (
            <Grid item xs={12} sm={6} md={4} key={strategy.id}>
              <Card sx={kobraCardSx}>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: alpha(K.blue, 0.12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <NotificationsIcon sx={{ fontSize: 28, color: K.blue }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
                          {strategy.nombre}
                        </Typography>
                        <Chip
                          label={strategy.canal?.toUpperCase() || 'WHATSAPP'}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 20,
                            fontWeight: 600,
                            backgroundColor: 'action.selected',
                          }}
                        />
                      </Box>
                    </Box>
                    <StatusBadge status={strategy.status} />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Tiempo:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getTimingInfo(strategy)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        label={
                          strategy.tipo === 'recordatorio' ? 'Recordatorio' :
                          strategy.tipo === 'cobro_atraso' ? 'Cobro Atraso' :
                          strategy.tipo || 'Recordatorio'
                        }
                        size="small"
                        color={strategy.tipo === 'cobro_atraso' ? 'warning' : 'primary'}
                        sx={{ fontWeight: 600 }}
                      />
                      {strategy.frecuencia && (
                        <Chip
                          label={
                            strategy.frecuencia === 'daily' ? 'Diario' :
                            strategy.frecuencia === 'weekly' ? 'Semanal' :
                            strategy.frecuencia === 'monthly' ? 'Mensual' :
                            strategy.frecuencia === 'one-time' ? 'Una vez' :
                            strategy.frecuencia || 'Diario'
                          }
                          size="small"
                          sx={{
                            fontWeight: 600,
                            backgroundColor: 'action.selected',
                          }}
                        />
                      )}
                    </Box>

                    {strategy.descripcion && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                          Descripción:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            color: 'text.primary',
                            lineHeight: 1.6,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {strategy.descripcion.length > 150
                            ? `${strategy.descripcion.substring(0, 150)}...`
                            : strategy.descripcion}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'flex-end',
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(strategy)}
                      sx={{
                        color: K.blue,
                        '&:hover': {
                          backgroundColor: alpha(K.blue, 0.1),
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(strategy.id)}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.light',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${K.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <NotificationsIcon sx={{ color: K.blue }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingStrategy ? 'Editar Estrategia' : 'Crear Estrategia'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: '#fff' }}>
          {/* Basic Information */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>Información Básica</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                  Nombre *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Nombre de la estrategia"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      backgroundColor: '#fff',
                    },
                  }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                  Descripción
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Descripción de la estrategia"
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      backgroundColor: '#fff',
                    },
                  }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                  Estado
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    sx={{
                      backgroundColor: '#fff',
                      borderRadius: 1,
                    }}
                  >
                    <MenuItem value="active">Activo</MenuItem>
                    <MenuItem value="inactive">Inactivo</MenuItem>
                    <MenuItem value="testing">Prueba</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                  Tipo
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    sx={{
                      backgroundColor: '#fff',
                      borderRadius: 1,
                    }}
                  >
                    <MenuItem value="recordatorio">Recordatorio</MenuItem>
                    <MenuItem value="cobro_atraso">Cobro Atraso</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Timing Configuration */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>Configuración de Tiempo</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {formData.tipo === 'recordatorio' && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                    Días Antes del Vencimiento
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.dias_antes_vencimiento}
                    onChange={(e) => setFormData({ ...formData, dias_antes_vencimiento: parseInt(e.target.value) || 0 })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                </Grid>
                )}
                {formData.tipo === 'cobro_atraso' && (
                  <>
                    <Grid item size={12} sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                        Frecuencia por tipo de préstamo (solo cobro por atraso)
                      </Typography>
                    </Grid>
                    <Grid item size={6} sx={{mt:2}}>  
                      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                        Préstamos diarios (cada X días)
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={formData.configuracion_atraso_tiempo.prestamos_diarios_cada_dias}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            configuracion_atraso_tiempo:{
                              ...formData.configuracion_atraso_tiempo,
                              prestamos_diarios_cada_dias: parseInt(e.target.value, 10) || 0,
                            }
                          })
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            backgroundColor: '#fff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item size={6} sx={{mt:2}}>  
                      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                        Préstamos semanales (cada X días)
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={formData.configuracion_atraso_tiempo.prestamos_semanales_cada_dias}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            configuracion_atraso_tiempo:{
                              ...formData.configuracion_atraso_tiempo,
                              prestamos_semanales_cada_dias: parseInt(e.target.value, 10) || 0,
                            }
                          })
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            backgroundColor: '#fff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item size={6} sx={{mt:2}}>  
                      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                        Préstamos quincenales (cada X días)
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={formData.configuracion_atraso_tiempo.prestamos_quincenales_cada_dias}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            configuracion_atraso_tiempo:{
                              ...formData.configuracion_atraso_tiempo,
                              prestamos_quincenales_cada_dias: parseInt(e.target.value, 10) || 0,
                            }
                          })
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            backgroundColor: '#fff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item size={6} sx={{mt:2}}>  
                      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                        Préstamos mensuales (cada X días)
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={formData.configuracion_atraso_tiempo.prestamos_mensuales_cada_dias}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            configuracion_atraso_tiempo:{
                              ...formData.configuracion_atraso_tiempo,
                              prestamos_mensuales_cada_dias: parseInt(e.target.value, 10) || 0,
                            }
                          })
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            backgroundColor: '#fff',
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Channel & Message */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>Canal y Mensaje</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item size={6}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                    Canal *
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.canal}
                      onChange={(e) => setFormData({ ...formData, canal: e.target.value })}
                      sx={{
                        backgroundColor: '#fff',
                        borderRadius: 1,
                      }}
                    >
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="llamada">Llamada</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={6}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                    Endpoint
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="URL del endpoint"
                    value={formData.endpoint}
                    onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                </Grid>
                <Grid item size={12}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                    Bot *
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.id_bot}
                      onChange={(e) => setFormData({ ...formData, id_bot: e.target.value })}
                      sx={{
                        backgroundColor: '#fff',
                        borderRadius: 1,
                      }}
                    >
                      <MenuItem value="">Seleccionar un bot</MenuItem>
                      {bots?.map((bot) => (
                        <MenuItem key={bot.id} value={bot.id}>
                          {bot.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

        
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending || !formData.nombre || !formData.id_bot}
            sx={kobraPrimaryBtnSx}
          >
            {createMutation.isPending || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      <ToastComponent />
    </Box>
  );
};

